// カメラ映像から QR コードを読み取るヘルパー（M4: Unity 受信待ち QR のスキャン用）。
// getUserMedia でカメラ映像を取得し、<video> に流し込んで <canvas> に描画、
// jsQR でデコードするループを requestAnimationFrame で回す。

import jsQR from "jsqr";

function stopTracks(stream: MediaStream): void {
  for (const track of stream.getTracks()) track.stop();
}

/** start() の途中で stop()（または別の start()）された場合に投げる。呼び出し側は name === "AbortError" で無視できる。 */
function abortError(): Error {
  const e = new Error("QR スキャンは停止されました。");
  e.name = "AbortError";
  return e;
}

export class QrScanner {
  private stream: MediaStream | null = null;
  private rafId: number | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private stopped = false;
  /** start/stop のたびに進める世代番号。非同期待ちの間に stop() されたかの判定に使う。 */
  private generation = 0;

  /**
   * カメラを起動し、指定の <video> 要素に流し込んでスキャンを開始する。
   * QR を検出するたびに onDetect を呼ぶ（呼び出し側で判定して continue/stop を決める）。
   * 既に起動中なら前回のカメラを止めてから起動し直す。
   * 起動待ちの間に stop() された場合は、取得したカメラを即座に止めて AbortError を投げる。
   * @throws Error カメラ非対応・権限拒否など
   */
  async start(videoEl: HTMLVideoElement, onDetect: (text: string) => void, onError?: (e: Error) => void): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("このブラウザはカメラに対応していません。");
    }

    // 二重 start: 前回のストリーム・ループを確実に止めてから始める
    this.stop();
    const gen = ++this.generation;
    this.stopped = false;
    const isCurrent = (): boolean => gen === this.generation;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
    } catch (e) {
      if (!isCurrent()) throw abortError();
      // 背面カメラが無い環境（PC 等）では既定カメラにフォールバック
      stream = await navigator.mediaDevices.getUserMedia({ video: true }).catch(() => {
        throw e;
      });
    }
    // getUserMedia 待ちの間に stop() / 別の start() が呼ばれていたら、取得したカメラを即座に止める
    if (!isCurrent()) {
      stopTracks(stream);
      throw abortError();
    }
    this.stream = stream;

    videoEl.srcObject = stream;
    videoEl.setAttribute("playsinline", "true");
    try {
      await videoEl.play();
    } catch (e) {
      // 再生失敗時もカメラを点けっぱなしにしない
      stopTracks(stream);
      if (isCurrent()) this.stop();
      throw e;
    }
    if (!isCurrent()) {
      stopTracks(stream);
      throw abortError();
    }

    this.canvas = document.createElement("canvas");
    const ctx = this.canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      this.stop();
      throw new Error("QR 読み取りの初期化に失敗しました。");
    }

    const tick = (): void => {
      if (this.stopped || !this.canvas || !isCurrent()) return;
      if (videoEl.readyState === videoEl.HAVE_ENOUGH_DATA) {
        const w = videoEl.videoWidth;
        const h = videoEl.videoHeight;
        if (w > 0 && h > 0) {
          this.canvas.width = w;
          this.canvas.height = h;
          ctx.drawImage(videoEl, 0, 0, w, h);
          try {
            const image = ctx.getImageData(0, 0, w, h);
            const code = jsQR(image.data, w, h, { inversionAttempts: "dontInvert" });
            if (code && code.data) {
              onDetect(code.data);
            }
          } catch (e) {
            onError?.(e instanceof Error ? e : new Error(String(e)));
          }
        }
      }
      // onDetect 内で stop() された場合は次フレームを予約しない
      if (this.stopped || !isCurrent()) return;
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  /** カメラを停止し、MediaStream のトラックを確実に止める（起動待ち中の start() も無効化する）。 */
  stop(): void {
    this.stopped = true;
    this.generation++;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.stream) {
      stopTracks(this.stream);
      this.stream = null;
    }
    this.canvas = null;
  }
}
